import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

type Flow = 'with_ai' | 'write_my_own';

type UseIntroResult = ModelHookResult<undefined, { submit: (flow: Flow) => void }>;

export function useIntro(): UseIntroResult {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	// A draft is "the user's" when there's draft content and no AI-authored text.
	// We key on ask/description (not recommendedDraft's truthiness) because image
	// regeneration writes recommendedDraft.image for write-my-own users too, which
	// would otherwise wipe their work in the write_my_own branch below.
	const hasAiContent = !!(data.draft?.recommendedDraft?.ask || data.draft?.recommendedDraft?.description);
	const hasUserDraft = !!((data.draft?.petitionDraft || data.draft?.editingDraft) && !hasAiContent);

	const submit = useCallback(
		(flow: Flow) => {
			void track('sap_intro_submit', { cta: flow });

			if (flow === 'write_my_own') {
				const draft = !hasUserDraft
					? { recommendedDraft: null, petitionDraft: null, editingDraft: null, metadata: { imageJobId: null } }
					: {};
				actions.send({ type: 'submit', target: 'user', draft });
				return;
			}

			actions.send({ type: 'submit', target: 'ai' });
		},
		[actions, hasUserDraft, track],
	);

	return {
		actions: {
			submit,
		},
	};
}
